import { assertUint8Arrays, getFromStore, putToStore, openDB, promisifyRequest } from "./Utils";
import { DB_NAME, DB_VERSION, setupSchema } from "./Schema";
import { encryptMessage,decryptMessage } from "../utils/CryptoHelpers";
import bs58 from 'bs58';
import { sha256 } from "js-sha256";

async function getDB() {
  return await openDB(DB_NAME, DB_VERSION, setupSchema);
}

/**
 * Checks whether a conversation entry exists in IndexedDB.
 * @param {Uint8Array} conversationId
 * @returns {Promise<boolean>}
 */
export async function conversationExists({ conversationId }) {
  const db = await getDB();
  const tx = db.transaction("conversations", "readonly");
  const store = tx.objectStore("conversations");
  const entry = await getFromStore(store, conversationId);
  return !!entry;
}

/**
 * Inserts or updates a conversation entry in IndexedDB.
 * - Encrypts all fields with a unique salt.
 * - If the conversation already exists, merges participants (ensuring uniqueness).
 * - This ensures that added participants are not lost on subsequent upserts.
 *
 * @param {Uint8Array} conversationId
 * @param {string} conversationName
 * @param {string} lastMessage
 * @param {Uint8Array[]} participants - array of Uint8Array view keys
 * @param {Uint8Array} senderViewPublicKey
 * @param {string} userId
 * @param {string} password
 * @returns {Promise<IDBValidKey>}
 */
export async function upsertConversationMeta({
  conversationId,
  conversationName,
  lastMessage,
  participants,
  senderViewPublicKey,
  userId,
  password,
}) {
  assertUint8Arrays({
    conversationId,
    ...(senderViewPublicKey ? { senderViewPublicKey } : {}),
  });

  const db = await getDB();

  const readTx = db.transaction("conversations", "readonly");
  const readStore = readTx.objectStore("conversations");
  const existing = await getFromStore(readStore, conversationId);

  let existingParticipants = [];
  let prevSalt;
  if (existing?.encParticipants && existing?.salt) {
    try {
      prevSalt = Uint8Array.from(atob(existing.salt), c => c.charCodeAt(0));
      const decrypted = await decryptMessage(existing.encParticipants, password, prevSalt);
      existingParticipants = JSON.parse(decrypted).map(bs58.decode);
    } catch {
      existingParticipants = [];
    }
  }

  // Merge and deduplicate participants
  const combined = [...existingParticipants, ...(participants || [])];
  const unique = Array.from(new Set(combined.map(b => bs58.encode(b))));
  const participantStrings = unique;

  // Prepare new salt and hash
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const saltB64 = btoa(String.fromCharCode(...salt));
  const userKeyHash = await sha256(userId + password);

  // Conditionally encrypt fields only if provided
  /*const encConversationName = conversationName != null
    ? (await encryptMessage({ message: conversationName, password, salt })).encryptedContent
    : existing?.encConversationName;

  const encLastMessage = lastMessage != null
    ? (await encryptMessage({ message: lastMessage, password, salt })).encryptedContent
    : existing?.encLastMessage;

  const encSenderViewPublicKey = senderViewPublicKey
    ? (await encryptMessage({ message: bs58.encode(senderViewPublicKey), password, salt })).encryptedContent
    : existing?.encSenderViewPublicKey;*/

    const reencryptIfNeeded = async (existingEncrypted, prevSalt) => {
      if (!existingEncrypted || !prevSalt) return null;
      try {
        const decrypted = await decryptMessage(existingEncrypted, password, prevSalt);
        return (await encryptMessage({ message: decrypted, password, salt })).encryptedContent;
      } catch {
        return null;
      }
    };

    const encConversationName = conversationName != null
      ? (await encryptMessage({ message: conversationName, password, salt })).encryptedContent
      : await reencryptIfNeeded(existing?.encConversationName, prevSalt);

    const encLastMessage = lastMessage != null
      ? (await encryptMessage({ message: lastMessage, password, salt })).encryptedContent
      : await reencryptIfNeeded(existing?.encLastMessage, prevSalt);

    const encSenderViewPublicKey = senderViewPublicKey
      ? (await encryptMessage({ message: bs58.encode(senderViewPublicKey), password, salt })).encryptedContent
      : await reencryptIfNeeded(existing?.encSenderViewPublicKey, prevSalt);


  const encParticipants = await encryptMessage({
    message: JSON.stringify(participantStrings),
    password,
    salt,
  });

  const writeTx = db.transaction("conversations", "readwrite");
  const writeStore = writeTx.objectStore("conversations");

  return putToStore(writeStore, {
    conversationId,
    encConversationName,
    userKeyHash,
    encLastMessage,
    encSenderViewPublicKey,
    encParticipants: encParticipants.encryptedContent,
    salt: saltB64,
  });
}

/**
 * Retrieves and decrypts metadata for a single conversation.
 * @param {Object} params
 * @param {Uint8Array} params.conversationId - The unique conversation ID
 * @param {string} params.password - The user's password for decryption
 * @returns {Promise<Object|null>} Decrypted conversation metadata or null if not found
 */
export async function getConversationMetaData({ conversationId, password }) {
  const db = await getDB();
  const tx = db.transaction("conversations", "readonly");
  const store = tx.objectStore("conversations");
  const entry = await getFromStore(store, conversationId);

  if (!entry) return null;

  try {
    return await decryptConversationEntry(entry, password);
  } catch (err) {
    console.warn("Failed to decrypt conversation metadata:", err);
    return null;
  }
}

/**
 * Retrieves and decrypts all conversation metadata for a given user.
 * @param {string} userId - The user's ID
 * @param {string} password - The user's password for decryption
 * @returns {Promise<Array<Object>>} Array of decrypted conversation metadata
 */
export async function getConversations(userId, password) {
  const userKeyHash = await sha256(userId + password);
  const db = await getDB();
  const tx = db.transaction("conversations", "readonly");
  const store = tx.objectStore("conversations");

  const index = store.index("userKeyHash");
  const raw = await promisifyRequest(index.getAll(IDBKeyRange.only(userKeyHash)));

  const results = [];

  for (const entry of raw) {
    try {
      const decrypted = await decryptConversationEntry(entry, password);
      results.push(decrypted);
    } catch (err) {
      console.warn("Corrupted conversation entry skipped:", err);
    }
  }

  return results;
}
/**
 * Decrypts a single conversation entry.
 *
 * Only attempts to decrypt fields if present.
 *
 * @param {Object} entry - Encrypted DB entry
 * @param {string} password
 * @returns {Promise<Object>}
 */
async function decryptConversationEntry(entry, password) {
  const {
    conversationId,
    encConversationName,
    encLastMessage,
    encSenderViewPublicKey,
    encParticipants,
    salt,
  } = entry;

  const saltBytes = Uint8Array.from(atob(salt), c => c.charCodeAt(0));

  const conversationName = encConversationName
    ? await decryptMessage(encConversationName, password, saltBytes)
    : null;

  const lastMessage = encLastMessage
    ? JSON.parse(await decryptMessage(encLastMessage, password, saltBytes))
    : null;

  const senderViewPublicKey = encSenderViewPublicKey
    ? bs58.decode(await decryptMessage(encSenderViewPublicKey, password, saltBytes))
    : null;

  const participants = encParticipants
    ? JSON.parse(await decryptMessage(encParticipants, password, saltBytes)).map(bs58.decode)
    : [];

  return {
    conversationId,
    conversationName,
    lastMessage,
    senderViewPublicKey,
    participants,
  };
}

export async function clearConversations() {
  const db = await getDB();
  const tx = db.transaction("conversations", "readwrite");
  const store = tx.objectStore("conversations");
  return promisifyRequest(store.clear());
}